import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { notificationsAPI, dashboardAPI } from '@/lib/api';
import logger from '@/lib/logger';
import {
  Shield, LayoutDashboard, Users, ClipboardList, MessageSquare,
  Receipt, Settings, LogOut, Bell, BellOff, ChevronLeft, ChevronRight, Menu,
  FileText, UserCircle, CalendarDays, Activity, AlertTriangle, Briefcase
} from 'lucide-react';

const SNOOZE_KEY = 'notif_snooze_until';
const SNOOZE_DURATION_MS = 60 * 60 * 1000; // 1 hour

const navItems = {
  admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Clients', path: '/dashboard/clients' },
    { icon: Briefcase, label: 'Directors', path: '/dashboard/directors' },
    { icon: ClipboardList, label: 'Task Boards', path: '/dashboard/boards' },
    { icon: AlertTriangle, label: 'Compliance Radar', path: '/dashboard/upcoming-due' },
    { icon: CalendarDays, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: MessageSquare, label: 'Chat', path: '/dashboard/chat' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: Receipt, label: 'Billing', path: '/dashboard/billing' },
    { icon: Activity, label: 'Activity Log', path: '/dashboard/activity-log' },
    { icon: UserCircle, label: 'Team', path: '/dashboard/team' },
    { icon: FileText, label: 'Leads', path: '/dashboard/leads' },
  ],
  staff: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Clients', path: '/dashboard/clients' },
    { icon: Briefcase, label: 'Directors', path: '/dashboard/directors' },
    { icon: ClipboardList, label: 'Task Boards', path: '/dashboard/boards' },
    { icon: AlertTriangle, label: 'Compliance Radar', path: '/dashboard/upcoming-due' },
    { icon: CalendarDays, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: MessageSquare, label: 'Chat', path: '/dashboard/chat' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
  ],
  client: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: ClipboardList, label: 'My Tasks', path: '/dashboard/boards' },
    { icon: CalendarDays, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: FileText, label: 'Documents', path: '/dashboard/documents' },
    { icon: MessageSquare, label: 'Chat', path: '/dashboard/chat' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: Receipt, label: 'Billing', path: '/dashboard/billing' },
  ],
  client_team: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: ClipboardList, label: 'My Tasks', path: '/dashboard/boards' },
    { icon: CalendarDays, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: FileText, label: 'Documents', path: '/dashboard/documents' },
    { icon: MessageSquare, label: 'Chat', path: '/dashboard/chat' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
  ],
};

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [upcomingDueCount, setUpcomingDueCount] = React.useState(0);
  const [snoozeUntil, setSnoozeUntil] = React.useState(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem(SNOOZE_KEY) : null;
    const ts = stored ? parseInt(stored, 10) : 0;
    return ts > Date.now() ? ts : 0;
  });
  const lastSeenIdsRef = React.useRef(new Set());
  const notifPermissionRef = React.useRef(null);
  const snoozeUntilRef = React.useRef(snoozeUntil);
  React.useEffect(() => { snoozeUntilRef.current = snoozeUntil; }, [snoozeUntil]);

  // Auto-clear stale snooze when its window expires (re-enables OS toasts).
  React.useEffect(() => {
    if (!snoozeUntil) return undefined;
    const ms = snoozeUntil - Date.now();
    if (ms <= 0) { setSnoozeUntil(0); localStorage.removeItem(SNOOZE_KEY); return undefined; }
    const t = setTimeout(() => { setSnoozeUntil(0); localStorage.removeItem(SNOOZE_KEY); }, ms);
    return () => clearTimeout(t);
  }, [snoozeUntil]);

  const isSnoozed = snoozeUntil > Date.now();
  const snoozeMinutesLeft = isSnoozed ? Math.max(1, Math.ceil((snoozeUntil - Date.now()) / 60000)) : 0;

  const handleSnoozeToggle = () => {
    if (isSnoozed) {
      setSnoozeUntil(0);
      localStorage.removeItem(SNOOZE_KEY);
    } else {
      const until = Date.now() + SNOOZE_DURATION_MS;
      setSnoozeUntil(until);
      localStorage.setItem(SNOOZE_KEY, String(until));
    }
  };

  const role = user?.role || 'client';
  const items = navItems[role] || navItems.client;

  // Request OS-level notification permission once per session (on first load).
  React.useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().then(p => { notifPermissionRef.current = p; });
    } else {
      notifPermissionRef.current = Notification.permission;
    }
  }, []);

  React.useEffect(() => {
    const loadNotifications = async () => {
      try {
        const [listRes, countRes] = await Promise.all([
          notificationsAPI.list({ limit: 20 }),
          notificationsAPI.unreadCount(),
        ]);
        const items = listRes.data?.items || [];
        const newUnreadCount = countRes.data?.unread || 0;

        // Fire OS toast for newly-arrived UNREAD notifications that we haven't seen yet.
        // Skip if user has snoozed (still shows in-app, just no popup).
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted' && snoozeUntilRef.current <= Date.now()) {
          const firstLoad = lastSeenIdsRef.current.size === 0;
          if (!firstLoad) {
            for (const n of items) {
              if (!n.is_read && !lastSeenIdsRef.current.has(n.id)) {
                try {
                  const t = new Notification(n.title || 'Growartha', {
                    body: n.body || '',
                    tag: n.id,
                    icon: '/favicon.ico',
                  });
                  t.onclick = () => {
                    window.focus();
                    if (n.link) navigate(n.link);
                    t.close();
                  };
                } catch (e) { /* no-op, some browsers throw on backgrounded tabs */ }
              }
            }
          }
          lastSeenIdsRef.current = new Set(items.map(n => n.id));
        }

        setNotifications(items);
        setUnreadCount(newUnreadCount);
      } catch (err) {
        logger.error('Failed to load notifications', err);
      }
    };
    const loadUpcomingDueCount = async () => {
      try {
        const res = await dashboardAPI.upcomingDue();
        setUpcomingDueCount((res.data || []).length);
      } catch (err) { logger.error('Failed to load upcoming due count', err); }
    };
    loadNotifications();
    loadUpcomingDueCount();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) { logger.error('Failed to mark all read', err); }
  };

  const handleNotificationClick = async (n) => {
    try {
      if (!n.is_read) await notificationsAPI.markRead(n.id);
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnreadCount(c => Math.max(0, c - (n.is_read ? 0 : 1)));
    } catch (err) {
      logger.warn('mark-read failed (non-blocking)', err);
    }
    if (n.link) navigate(n.link);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';

  return (
    <div className="page-shell flex h-screen overflow-hidden">
      {/* Sidebar - Desktop */}
      <aside className={`hidden md:flex flex-col border-r bg-card transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}>
        <div className="flex items-center gap-2 px-4 h-16 border-b">
          <Shield className="h-6 w-6 text-primary flex-shrink-0" />
          {!collapsed && <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Growartha</span>}
        </div>
        
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {items.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="h-4.5 w-4.5 flex-shrink-0" style={{ width: '18px', height: '18px' }} />
                  {!collapsed && (
                    <span className="flex-1">{item.label}</span>
                  )}
                  {!collapsed && item.label === 'Compliance Radar' && upcomingDueCount > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-red-100 text-red-700'}`}>{upcomingDueCount}</span>
                  )}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="border-t p-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors duration-150"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 border-b bg-card flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-sidebar-toggle">
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
              {items.find(i => i.path === location.pathname || (i.path !== '/dashboard' && location.pathname.startsWith(i.path)))?.label || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative" data-testid="topbar-notifications-button">
                  {isSnoozed
                    ? <BellOff className="h-4.5 w-4.5 text-muted-foreground" style={{ width: '18px', height: '18px' }} />
                    : <Bell className="h-4.5 w-4.5" style={{ width: '18px', height: '18px' }} />}
                  {unreadCount > 0 && !isSnoozed && (
                    <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80" data-testid="notifications-panel">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="font-semibold text-sm">Notifications</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={handleSnoozeToggle}
                      data-testid="notif-snooze-toggle"
                      title={isSnoozed ? `Snoozed for ${snoozeMinutesLeft} more min — click to resume` : 'Mute popups for 1 hour'}
                      className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                        isSnoozed
                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {isSnoozed
                        ? <><BellOff className="h-3 w-3" />{snoozeMinutesLeft}m</>
                        : <><BellOff className="h-3 w-3" />Snooze 1h</>}
                    </button>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" className="text-xs" onClick={handleMarkAllRead}>Mark all read</Button>
                    )}
                  </div>
                </div>
                <Separator />
                <ScrollArea className="max-h-72">
                  {notifications.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No notifications</p>
                  ) : (
                    notifications.slice(0, 10).map(n => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => handleNotificationClick(n)}
                        className={`w-full text-left px-3 py-2.5 text-sm border-b last:border-0 hover:bg-muted/40 transition-colors ${!n.is_read ? 'bg-accent/40' : ''}`}
                        data-testid={`notification-item-${n.id}`}
                      >
                        <p className="font-medium text-foreground line-clamp-2">{n.title}</p>
                        {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-muted-foreground">
                            {n.actor_name ? `${n.actor_name} · ` : ''}{new Date(n.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                        </div>
                      </button>
                    ))
                  )}
                </ScrollArea>
                <Separator />
                <Link
                  to="/dashboard/notifications"
                  className="block text-center py-2 text-xs font-medium text-primary hover:bg-muted/40"
                  data-testid="notifications-view-all"
                >
                  View all notifications →
                </Link>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  {user?.name && <span className="text-sm font-medium hidden sm:inline">{user.name}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <Badge variant="secondary" className="mt-1 text-xs">{role}</Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-background">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-card border-r">
            <div className="flex items-center gap-2 px-4 h-16 border-b">
              <Shield className="h-6 w-6 text-primary" />
              <span className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Growartha</span>
            </div>
            <nav className="p-2 space-y-1">
              {items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                  >
                    <item.icon className="h-4 w-4" />
                    <span className="flex-1">{item.label}</span>
                    {item.label === 'Compliance Radar' && upcomingDueCount > 0 && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-red-100 text-red-700'}`}>{upcomingDueCount}</span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
